import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridSearchIcon } from '@mui/x-data-grid';
import CommonTable from 'components/common/CommonTable'; // Adjust the path as per your setup
import { Box, Button, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { Icon } from '@iconify/react';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import { axios } from 'utils/axios';
import VehicleFiltersDrawer from 'components/_dashboard/vehicle/VehicleFilterDrawer';
import { CheckCircle, Cancel, DirectionsCar } from '@mui/icons-material';
import { Container } from '@mui/material';
import useSettings from 'hooks/useSettings';
import CustomPaper from 'components/common/CustomPaper';

const defaultFilters = {
  search: '',
  dateFrom: '',
  dateTo: '',
  VehicleType: '',
  pageSize: 10,
  Owner: '',
  DefaultDriver: '',
  ClientDefaultName: '',
  Tare: '',
  CurrentStatus: '',
  Haulier: '',
  RegistrationPlate: '',
  CompanyID: ''
};
const Vehicles = () => {
  const navigate = useNavigate();
  const { themeStretch } = useSettings();

  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(defaultFilters);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const { enqueueSnackbar } = useSnackbar();

  const [totalRows, setTotalRows] = useState(0);

  const columns = [
    { field: 'RegistrationPlate', headerName: 'Registration Plate', flex: 1, minWidth: 250 },
    { field: 'VehicleRef', headerName: 'Vehicle Ref', flex: 1, minWidth: 200 },
    { field: 'VehicleType', headerName: 'Vehicle Type', flex: 1, minWidth: 200 },
    { field: 'Haulier', headerName: 'Haulier', flex: 1, minWidth: 200 },
    { field: 'ClientDefaultName', headerName: 'Client', flex: 1, minWidth: 200 },
    { field: 'DefaultDriver', headerName: 'Default Driver', flex: 1, minWidth: 200 },
    { field: 'InternalExternal', headerName: 'Internal/External', flex: 1, minWidth: 200 },
    { field: 'CurrentStatus', headerName: 'Current Status', flex: 1, minWidth: 190 }
  ];

  const handleCreateContact = () => {
    navigate('/dashboard/vehicles/new');
  };

  const handleRowClick = (params: any) => {
    navigate(`/dashboard/vehicles/details/${params.row.Id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/dashboard/vehicles/edit/${id}`);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    fetchVehicles(true);
  };

  const fetchVehicles = async (isClear?: boolean, searchFilters?: any) => {
    setLoading(true);
    try {
      const appliedFilters: any = isClear
        ? defaultFilters
        : searchFilters
        ? searchFilters
        : filters;

      const query = new URLSearchParams({
        page: page.toString(),
        ...Object.entries(appliedFilters)
          .filter(([key, value]) => value !== '' && value !== null && value !== undefined)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      }).toString();

      const response = await axios.get(`/rest/api/vehicle?${query}`);
      const data = response.data.data || [];
      setVehicles(data);
      setTotalRows(response.data.pageMeta.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    try {
      await axios.delete(`/rest/api/vehicle/${id}`);
      const newVehicles = vehicles.filter((el: any) => el.Id !== id);
      setVehicles(newVehicles);
      enqueueSnackbar('Delete Success', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Delete Failed', { variant: 'success' });
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.toLowerCase();
    const updatedFilters = { ...filters, search: searchValue };
    setFilters(updatedFilters);

    // Debounce API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchVehicles(false, updatedFilters); // Pass the latest value directly
    }, 500); // Adjust debounce time as needed (500ms in this case)
  };

  useEffect(() => {
    fetchVehicles();
  }, [page, pageSize]);

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Box
        sx={{
          padding: 3,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3
          }}
        >
          <HeaderBreadcrumbs
            heading="Vehicles"
            links={[
              { name: 'Dashboard', href: '/dashboard' },
              { name: 'Vehicles', href: '/dashboard/vehicles' }
            ]}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 2, // Adds spacing between the buttons
              alignItems: 'center'
            }}
          >
            <Button variant="contained" onClick={handleCreateContact} startIcon={<DirectionsCar />}>
              Create Vehicle
            </Button>
            <Tooltip title="Add Filters">
              <Button
                disableRipple
                color="inherit"
                variant="outlined"
                endIcon={<Icon icon={roundFilterList} />}
                onClick={() => setDrawerOpen(true)}
              >
                Filters
              </Button>
            </Tooltip>
          </Box>
        </Box>
        <CustomPaper>
          <TextField
            variant="outlined"
            placeholder="Search..."
            size="medium"
            sx={{ width: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <GridSearchIcon />
                </InputAdornment>
              )
            }}
            value={filters.search}
            onChange={handleSearch}
          />
          <Box>
            <CommonTable
              rows={vehicles}
              columns={columns}
              pageSize={filters.pageSize}
              totalRecords={totalRows}
              loading={loading}
              handlePageChange={(newPage) => setPage(newPage + 1)}
              handlePageSizeChange={(newPageSize) => {
                console.log(newPageSize, 'This is the new pages size');
                setPageSize(newPageSize);
                setFilters({ ...filters, pageSize: newPageSize });
              }}
              onEdit={handleEdit}
              onDelete={(id) => handleDeleteVehicle(id)}
              onRowClick={handleRowClick}
            />
          </Box>
        </CustomPaper>
        <VehicleFiltersDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          filters={filters}
          setFilters={setFilters}
          onApply={() => {
            setDrawerOpen(false);
            fetchVehicles();
          }}
          onClear={clearFilters}
        />
      </Box>
    </Container>
  );
};

export default Vehicles;
